import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  CardActionArea,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Send, Add, Search, Close } from "@mui/icons-material";
import Header from "../components/Layout/Header";
import SprintPlanning from "../components/Dashboard/SprintPlanning";
import BacklogView from "../components/Dashboard/BacklogView";
import MIDashView from "../components/Dashboard/MIDashView";
import AISubmissionsView from "../components/Dashboard/AISubmissionsView";
import { dashboardAPI, configurationsAPI } from "../services/api";
import { stripHtml } from "../utils/htmlUtils";

const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tab from URL params, default to 0 (MI Dash)
  const tabFromUrl = parseInt(searchParams.get("tab") || "0", 10);
  const [tabValue, setTabValue] = useState(tabFromUrl);
  const [configurations, setConfigurations] = useState([]);
  // Load selected configuration from localStorage on mount (persists across page refreshes)
  const [selectedConfiguration, setSelectedConfiguration] = useState(() => {
    const saved = localStorage.getItem("selectedConfiguration");
    return saved ? parseInt(saved, 10) : null;
  });
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [changeRequests, setChangeRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayLimit, setDisplayLimit] = useState(12); // Show 12 cards initially (3x4 grid)

  useEffect(() => {
    loadConfigurations();
  }, []);

  // Sync tab value with URL params when they change
  useEffect(() => {
    const tabFromUrl = parseInt(searchParams.get("tab") || "0", 10);
    if (tabFromUrl !== tabValue) {
      setTabValue(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedConfiguration) {
      loadChangeRequests();
    }
  }, [selectedConfiguration]);

  // Refresh change requests when switching to tab 1 (Change Requests tab)
  useEffect(() => {
    if (tabValue === 1 && selectedConfiguration) {
      loadChangeRequests();
    }
  }, [tabValue, selectedConfiguration]);

  const loadConfigurations = async () => {
    try {
      const response = await configurationsAPI.getAll();
      setConfigurations(response.data);

      // Restore from localStorage or default to first configuration
      if (response.data.length > 0) {
        const savedConfigId = localStorage.getItem("selectedConfiguration");
        const savedConfigIdNum = savedConfigId
          ? parseInt(savedConfigId, 10)
          : null;

        // Check if saved configuration still exists
        const savedConfigExists =
          savedConfigIdNum &&
          response.data.some((c) => c.id === savedConfigIdNum);

        if (savedConfigExists) {
          setSelectedConfiguration(savedConfigIdNum);
        } else if (!selectedConfiguration) {
          // No saved config or it doesn't exist, use first one
          const firstConfigId = response.data[0].id;
          setSelectedConfiguration(firstConfigId);
          // Save the default selection
          localStorage.setItem(
            "selectedConfiguration",
            firstConfigId.toString(),
          );
        }
      }
    } catch (err) {
      console.error("Failed to load configurations:", err);
    }
  };

  const loadChangeRequests = async () => {
    if (!selectedConfiguration) {
      setLoadingRequests(false);
      return;
    }
    setLoadingRequests(true);
    try {
      const response = await dashboardAPI.getChangeRequests(
        selectedConfiguration,
      );
      setChangeRequests(response.data);
    } catch (err) {
      console.error("Failed to load change requests:", err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedConfiguration) {
      setError("Please select a configuration first");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await dashboardAPI.createChangeRequest({
        title,
        description,
        template_content: templateContent,
        configuration_id: selectedConfiguration,
      });
      setSuccess("Change request submitted successfully! Analysis completed.");
      setTitle("");
      setDescription("");
      setTemplateContent("");
      loadChangeRequests();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit change request");
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Filter and sort change requests
  const filteredAndSortedRequests = useMemo(() => {
    let filtered = changeRequests;

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (req) =>
          req.title?.toLowerCase().includes(term) ||
          stripHtml(req.description || "").toLowerCase().includes(term)
      );
    }

    // Sort by newest first (created_at descending)
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });

    return filtered;
  }, [changeRequests, searchTerm]);

  const displayedRequests = filteredAndSortedRequests.slice(0, displayLimit);
  const hasMore = filteredAndSortedRequests.length > displayLimit;

  return (
    <Box>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4" component="h1">
            Change Request Dashboard
          </Typography>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Configuration</InputLabel>
            <Select
              value={selectedConfiguration || ""}
              onChange={(e) => {
                const configId = e.target.value;
                setSelectedConfiguration(configId);
                // Save to localStorage for persistence across page refreshes and browser restarts
                localStorage.setItem(
                  "selectedConfiguration",
                  configId.toString(),
                );
              }}
              label="Configuration"
            >
              {configurations.map((config) => (
                <MenuItem key={config.id} value={config.id}>
                  {config.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {!selectedConfiguration && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Please select a configuration to continue. You can create
            configurations in the Settings page.
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => {
              setTabValue(newValue);
              // Update URL with tab parameter
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.set("tab", newValue.toString());
              setSearchParams(newSearchParams, { replace: true });
            }}
          >
            <Tab label="MI Dash" />
            <Tab label="Change Requests" />
            <Tab label="Sprint Planning" />
            <Tab label="Backlog" />
            <Tab label="AI Submissions" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <MIDashView activeConfigurationId={selectedConfiguration} />
        )}

        {tabValue === 1 && (
          <Box>
            {/* Header with Search and Submit Button */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
              flexWrap="wrap"
              gap={2}
            >
              <TextField
                placeholder="Search change requests..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm("")}
                        edge="end"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300, flexGrow: 1, maxWidth: 500 }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setSubmitDialogOpen(true)}
                disabled={!selectedConfiguration}
              >
                Submit New Change Request
              </Button>
            </Box>

            {/* Change Request Cards Grid */}
            {loadingRequests ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : displayedRequests.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchTerm
                    ? "No change requests match your search"
                    : "No change requests yet"}
                </Typography>
                {!searchTerm && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setSubmitDialogOpen(true)}
                    sx={{ mt: 2 }}
                    disabled={!selectedConfiguration}
                  >
                    Create Your First Change Request
                  </Button>
                )}
              </Paper>
            ) : (
              <>
                <Grid container spacing={3}>
                  {displayedRequests.map((request) => (
                    <Grid item xs={12} sm={6} md={4} key={request.id}>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                          transition: "transform 0.2s, box-shadow 0.2s",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: 6,
                          },
                        }}
                        onClick={() => {
                          localStorage.setItem(
                            "dashboardTab",
                            tabValue.toString(),
                          );
                          navigate(`/change-request/${request.id}`, {
                            state: { tab: tabValue },
                          });
                        }}
                      >
                        <CardActionArea sx={{ flexGrow: 1, p: 2 }}>
                          <CardContent sx={{ flexGrow: 1, p: 0 }}>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              gutterBottom
                              sx={{
                                mb: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {request.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mb: 2, display: "block" }}
                            >
                              Generated: {formatDate(request.created_at)}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                minHeight: 60,
                              }}
                            >
                              {request.description
                                ? (() => {
                                    const plainText = stripHtml(
                                      request.description,
                                    );
                                    return plainText || "No description";
                                  })()
                                : "No description"}
                            </Typography>
                            <Box
                              display="flex"
                              gap={1}
                              flexWrap="wrap"
                              alignItems="center"
                            >
                              {request.estimated_hours && (
                                <Chip
                                  label={`${request.estimated_hours}h`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {request.estimated_days && (
                                <Chip
                                  label={`${request.estimated_days}d`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {request.estimated_story_points && (
                                <Chip
                                  label={`${request.estimated_story_points} SP`}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                              )}
                              <Chip
                                label={request.status}
                                size="small"
                                color={
                                  request.status === "Completed"
                                    ? "success"
                                    : "default"
                                }
                                sx={{ ml: "auto" }}
                              />
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Load More Button */}
                {hasMore && (
                  <Box display="flex" justifyContent="center" mt={4}>
                    <Button
                      variant="outlined"
                      onClick={() => setDisplayLimit(displayLimit + 12)}
                    >
                      Load More ({filteredAndSortedRequests.length - displayLimit} remaining)
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* Submit Change Request Dialog */}
            <Dialog
              open={submitDialogOpen}
              onClose={() => {
                setSubmitDialogOpen(false);
                setError("");
                setSuccess("");
              }}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">Submit New Change Request</Typography>
                  <IconButton
                    onClick={() => {
                      setSubmitDialogOpen(false);
                      setError("");
                      setSuccess("");
                    }}
                    size="small"
                  >
                    <Close />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                {!selectedConfiguration && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Please select a configuration above to submit change
                    requests.
                  </Alert>
                )}
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
                    {success}
                  </Alert>
                )}
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    label="Title"
                    variant="outlined"
                    margin="normal"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    variant="outlined"
                    margin="normal"
                    multiline
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Change Request Template (Optional)"
                    variant="outlined"
                    margin="normal"
                    multiline
                    rows={6}
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    placeholder="Describe the type of change you would like to make..."
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setSubmitDialogOpen(false);
                    setError("");
                    setSuccess("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  startIcon={<Send />}
                  disabled={loading || !selectedConfiguration}
                >
                  {loading ? "Analysing..." : "Submit & Analyse"}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {tabValue === 2 && (
          <SprintPlanning activeConfigurationId={selectedConfiguration} />
        )}

        {tabValue === 3 && (
          <BacklogView
            activeConfigurationId={selectedConfiguration}
            onWorkItemsSubmitted={loadChangeRequests}
          />
        )}

        {tabValue === 4 && (
          <AISubmissionsView activeConfigurationId={selectedConfiguration} />
        )}
      </Container>
    </Box>
  );
};

export default DashboardPage;
